import React, { useState } from 'react';
import { Phone, Menu, X, Clock } from 'lucide-react';
import { wpMedia } from '../utils/wpMedia';

const aquifyLogo = wpMedia('2026/06/aquify-dsis-logo.png');

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <style>{`
        /* Nav links — dark on white background */
        .aq-nav-link {
          color: #1e293b;
          font-weight: 500;
          transition: color 0.2s;
          text-decoration: none;
        }
        .aq-nav-link:hover {
          color: #1571BA;
        }

        /* MWS badge pill */
        .aq-mws-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #F0F7FF;
          border: 1px solid #BFDBFE;
          border-radius: 8px;
          padding: 4px 10px 4px 6px;
          flex-shrink: 0;
        }
        .aq-mws-label {
          color: #0C4288;
          font-size: 0.62rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.07em;
          line-height: 1.25;
          white-space: nowrap;
        }
      `}</style>

      {/* ── Top Bar — gradient ───────────────────────────────────── */}
      <div
        style={{
          background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 60%, #2A9AD8 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.12)',
          padding: '0.45rem 1rem',
        }}
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:gap-5 text-sm flex-wrap gap-1" style={{ color: '#ffffff' }}>
          <div className="flex items-center">
            <Phone className="h-4 w-4 mr-2" style={{ color: '#90D8F5', flexShrink: 0 }} />
            <span className="font-medium">Call Us: 516-729-9013</span>
          </div>
          <span className="hidden md:inline" style={{ color: 'rgba(255,255,255,0.35)' }}>|</span>
          <div className="flex items-center">
            <svg className="h-4 w-4 mr-2 flex-shrink-0" style={{ color: '#90D8F5' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
            <a href="mailto:info@aquify.org" className="font-medium hover:underline" style={{ color: '#ffffff' }}>info@aquify.org</a>
          </div>
          <span className="hidden lg:inline" style={{ color: 'rgba(255,255,255,0.35)' }}>|</span>
          <div className="hidden lg:flex items-center">
            <Clock className="h-4 w-4 mr-2" style={{ color: '#90D8F5', flexShrink: 0 }} />
            <span className="font-medium">Mon–Fri: 7 AM–8 PM | Sat: 8 AM–1 PM | Sun: Closed</span>
          </div>
        </div>
      </div>

      {/* ── Main Header — white ──────────────────────────────────── */}
      <header
        className="sticky top-0 z-50"
        style={{
          background: '#ffffff',
          borderBottom: '1px solid #E5E7EB',
          boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-2.5">
          <div className="flex items-center justify-between">

            {/* ── LEFT: Brand group (logo + MWS badge) ── */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>

              {/* Aquify logo — blue version for white background */}
              <a href="/" style={{ display: 'block', lineHeight: 0 }}>
                <img
                  src={aquifyLogo}
                  alt="Aquify — Design, Sales, Installation & Service"
                  style={{ height: '62px', width: 'auto', objectFit: 'contain', display: 'block' }}
                />
              </a>

              {/* Master Water Specialist badge */}
              <div className="aq-mws-badge">
                <img
                  src={wpMedia('2026/06/mws_color_transparent.png')}
                  alt="Master Water Specialist"
                  style={{ height: '34px', width: 'auto', objectFit: 'contain', borderRadius: '4px', display: 'block' }}
                />
                <span className="aq-mws-label hidden sm:block">
                  Master Water<br />Specialist
                </span>
              </div>

            </div>

            {/* ── RIGHT: Desktop nav ── */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="/services" className="aq-nav-link">Services</a>
              <a href="/about" className="aq-nav-link">About</a>
              <a href="/water-guide" className="aq-nav-link">Water Guide</a>
              <a href="/newsletter" className="aq-nav-link">Newsletter</a>
              <a href="/contact" className="aq-nav-link">Contact</a>
            </nav>

            {/* ── RIGHT: Mobile hamburger ── */}
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
              style={{ color: '#1e293b', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

          </div>

          {/* ── Mobile dropdown nav ── */}
          {mobileMenuOpen && (
            <nav
              className="md:hidden flex flex-col gap-4 pt-4 pb-2"
              style={{ borderTop: '1px solid #E5E7EB', marginTop: '0.75rem' }}
            >
              <a href="/services" className="aq-nav-link" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="/about" className="aq-nav-link" onClick={() => setMobileMenuOpen(false)}>About</a>
              <a href="/water-guide" className="aq-nav-link" onClick={() => setMobileMenuOpen(false)}>Water Guide</a>
              <a href="/newsletter" className="aq-nav-link" onClick={() => setMobileMenuOpen(false)}>Newsletter</a>
              <a href="/contact" className="aq-nav-link" onClick={() => setMobileMenuOpen(false)}>Contact</a>
            </nav>
          )}

        </div>
      </header>
    </>
  );
};

export default Header;
